import { createContext, useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const MonitoreoContext = createContext();

export function MonitoreoProvider(props) {
    const { tokenR } = useGeneral();
    const llamadasRef = useRef([]);

    // Hierarchy filter state
    const [hierAreas, setHierAreas] = useState([]);
    const [hierLinesByArea, setHierLinesByArea] = useState([]);
    const [hierCeldasList, setHierCeldasList] = useState([]);
    const [selectedHierArea, setSelectedHierAreaState] = useState(null);
    const [selectedHierLinea, setSelectedHierLineaState] = useState(null);
    const [selectedHierCelda, setSelectedHierCeldaState] = useState(null);
    const selectedHierAreaRef = useRef(null);
    const selectedHierLineaRef = useRef(null);
    const selectedHierCeldaRef = useRef(null);

    useEffect(() => { selectedHierAreaRef.current = selectedHierArea; }, [selectedHierArea]);
    useEffect(() => { selectedHierLineaRef.current = selectedHierLinea; }, [selectedHierLinea]);
    useEffect(() => { selectedHierCeldaRef.current = selectedHierCelda; }, [selectedHierCelda]);

    const [celdas, setCeldas] = useState([]);
    const [celda, setCelda] = useState('');
    const celdaRef = useRef('');
    const [intervalo, setIntervalo] = useState(0);
    const [statusLinea, setStatusLinea] = useState([]);
    const [isLoadingStatusLinea, setIsLoadingStatusLinea] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [eficiYUti, setEficiYUti] = useState([]);
    const [llamadasActivas, setLlamadasActivas] = useState([]);
    const [isLoadingTable, setIsLoadingTable] = useState(false);
    const [corridaActual, setCorridaActual] = useState([]);
    const [isCorridaActive, setIsCorridaActive] = useState(false);
    const [corridaRequiresLinea, setCorridaRequiresLinea] = useState(false);

    const [dataP, setDataP] = useState([]);

    const [minX, setMinX] = useState(0);
    const [maxX, setMaxX] = useState(0);

    const [dataEfi, setDataEfi] = useState([]);
    const [dataUti, setDataUti] = useState([]);
    const [xEfiUti, setXEfiUti] = useState([]);
    const [value, setValue] = useState(0);
    const intervaloRef = useRef(intervalo);

    const [promedioResumen, setPromedioResumen] = useState({
        tiempoRespuesta: 0,
        resumenMantenimiento: 0,
        resumenCalidad: 0,
        resumenAlmacen: 0,
        resumenProduccion: 0,
    });

    const statusLineaAbortRef = useRef(null);
    const llamadasActivasAbortRef = useRef(null);
    const estyContAbortRef = useRef(null);
    const promedioResumenAbortRef = useRef(null);

    // Mantener el ref actualizado cuando cambia intervalo
    useEffect(() => {
        intervaloRef.current = intervalo;
    }, [intervalo]);

    useEffect(() => {
        celdaRef.current = celda;
    }, [celda]);

    const getCeldas = async (uToken) => {
        try {
            const data = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/celdas`, {
                method: 'GET',
                headers: {
                    authorization: `Bearer ${uToken}`,
                    Accept: 'application/json',
                },
            });
            if (!data.ok) return;
            const list = await data.json();
            if (Array.isArray(list) && list.length > 0) {
                setCeldas(list);
                setCelda((prev) => {
                    if (list.some((c) => c.celda1 === prev)) {
                        return prev;
                    }
                    if (list.some((c) => c.celda1 === 2)) {
                        return 2;
                    }
                    return list[0].celda1;
                });
            }
        } catch {
            // evita que la vista se congele si falla el servicio
        }
    };

    const getAreas = useCallback(async (uToken) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/areas`, {
                method: 'GET',
                headers: { authorization: `Bearer ${uToken}`, Accept: 'application/json' },
            });
            if (res.ok) setHierAreas(await res.json());
        } catch { /* ignore */ }
    }, []);

    const setSelectedHierArea = useCallback(async (area) => {
        setSelectedHierAreaState(area);
        setSelectedHierLineaState(null);
        setSelectedHierCeldaState(null);
        setHierCeldasList([]);
        setHierLinesByArea([]);
        if (!area) return;
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/lineas?area=${area.area1}`,
                { method: 'GET', headers: { authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' } }
            );
            if (res.ok) setHierLinesByArea(await res.json());
        } catch { /* ignore */ }
    }, [tokenR]);

    const setSelectedHierLinea = useCallback((linea) => {
        setSelectedHierLineaState(linea);
        setSelectedHierCeldaState(null);
        setHierCeldasList([]);
        if (!linea) return;
        fetch(
            `${process.env.REACT_APP_SERVICE_ENDPOINT}/CeldaPorLinea/CeldaPorLinea?linea=${linea.id}`,
            { method: 'GET', headers: { authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' } }
        ).then((res) => {
            if (!res.ok) return;
            res.json().then((raw) => {
                const celdas = raw
                    .flatMap((l) => l.celdas || [])
                    .filter((c) => c.orden !== 0)
                    .map((c) => ({ id: c.celda, nombre: c.nombreCelda }));
                setHierCeldasList(celdas);
            });
        }).catch(() => { /* ignore */ });
    }, [tokenR]);

    const setSelectedHierCelda = useCallback((celda) => {
        setSelectedHierCeldaState(celda);
    }, []);

    const getStatusLinea = useCallback(async (calls, uToken) => {
        statusLineaAbortRef.current?.abort();
        const controller = new AbortController();
        statusLineaAbortRef.current = controller;

        setIsLoadingStatusLinea(true);
        try {
            const data = {
                cantHoras: intervaloRef.current,
                ...(selectedHierAreaRef.current && { area: selectedHierAreaRef.current.area1 }),
                ...(selectedHierLineaRef.current && { linea: selectedHierLineaRef.current.id }),
                ...(selectedHierCeldaRef.current && { celda: selectedHierCeldaRef.current.id }),
            };

            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Paros/GetEstatusLineas`,
                {
                    method: 'POST',
                    body: JSON.stringify(data),
                    signal: controller.signal,
                    headers: {
                        authorization: `Bearer ${uToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!res.ok) return;
            const respuesta = await res.json();
            let estLinea = [];
            let estLineaT = [];
            let nEstatus;
            if (respuesta.length !== 0) {
                respuesta.forEach((resp) => {
                    {
                        resp.datos.forEach((dato) => {
                            if (dato.estatus === 0) {
                                nEstatus = 'Fuera de Operacion';
                            } else if (dato.estatus === 1) {
                                nEstatus = 'Operando';
                            } else if (dato.estatus === 2) {
                                nEstatus = 'Setup';
                            } else if (dato.estatus === 3) {
                                nEstatus = 'Purga';
                            } else if (dato.estatus === 4) {
                                nEstatus = 'Purga y Lavado';
                            } else if (dato.estatus === 5) {
                                nEstatus = 'Paro planeado';
                            } else if (dato.estatus === 7) {
                                nEstatus = 'Sin Material';
                            } else if (dato.estatus === 8) {
                                nEstatus = 'Atasco de Línea';
                            } else if (
                                dato.estatus === 6 ||
                                dato.estatus >= 9 ||
                                dato.estatus < 0
                            ) {
                                let nombreE = calls.find(
                                    (llamada) => llamada.tipoLlamada === dato.estatus
                                );
                                if (nombreE === undefined || nombreE.tipoLlamada === 10) {
                                    nEstatus = 'Paro';
                                } else {
                                    nEstatus = nombreE.nombre;
                                }
                            }
                            estLinea.push({
                                folio: dato.folio,
                                estatus: dato.estatus,
                                nombreEstatus: nEstatus,
                                fechaHoraFin: dato.fechaHoraFin,
                                fechaHoraIni: dato.fechaHoraIni,
                            });
                        });
                        estLineaT.push({
                            celda: resp.celda,
                            nombre: resp.nombre,
                            datos: estLinea,
                        });
                    }

                    estLinea = [];
                });
            }

            setStatusLinea(estLineaT);
        } catch (e) {
            if (e.name !== 'AbortError') {
                // evita congelar la vista si el servicio falla
            }
        } finally {
            if (!controller.signal.aborted) {
                setIsLoadingStatusLinea(false);
            }
        }
    }, []);

    const getLlamadas = useCallback(async (uToken) => {
        try {
            const data = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/tiposllamada/Listado`,
                {
                    method: 'GET',
                    headers: {
                        authorization: `Bearer ${uToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!data.ok) return;
            const calls = await data.json();
            if (Array.isArray(calls)) {
                llamadasRef.current = calls;
                getStatusLinea(calls, uToken);
            }
        } catch {
            // evita congelar la vista si el servicio falla
        }
    }, [getStatusLinea]);

    const getEstyCont = useCallback(async (uToken) => {
        const linea = selectedHierLineaRef.current;
        const area = selectedHierAreaRef.current;

        if (!linea) {
            setIsCorridaActive(false);
            setCorridaRequiresLinea(!!area);
            return;
        }

        setCorridaRequiresLinea(false);
        estyContAbortRef.current?.abort();
        const controller = new AbortController();
        estyContAbortRef.current = controller;

        try {
            const data = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetConteoCorrida?linea=${linea.id}`,
                {
                    method: 'GET',
                    signal: controller.signal,
                    headers: {
                        authorization: `Bearer ${uToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!data.ok) {
                setIsCorridaActive(false);
                return;
            }
            const values = await data.json();
            if (Array.isArray(values) && values.length === 0) {
                setIsCorridaActive(false);
                return;
            }
            setIsCorridaActive(true);
            const item = values[0];
            const toLocal = (str) => {
                if (!str) return str;
                const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(str.trim());
                return new Date(hasZone ? str : `${str}Z`);
            };
            setCorridaActual({ ...item, fechaIniReal: toLocal(item.fechaIniReal), fechaFinEstimada: toLocal(item.fechaFinEstimada) });
        } catch (e) {
            if (e.name !== 'AbortError') {
                setIsCorridaActive(false);
            }
        }
    }, []);

    const getLlamadasActivas = useCallback(async (uToken) => {
        llamadasActivasAbortRef.current?.abort();
        const controller = new AbortController();
        llamadasActivasAbortRef.current = controller;

        try {
            const params = new URLSearchParams();
            if (selectedHierAreaRef.current) params.append('area', selectedHierAreaRef.current.area1);
            if (selectedHierLineaRef.current) params.append('linea', selectedHierLineaRef.current.id);
            if (selectedHierCeldaRef.current) params.append('celda', selectedHierCeldaRef.current.id);
            const qs = params.toString() ? `?${params.toString()}` : '';
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/ListaLlamados/LlamadasActivas${qs}`,
                {
                    method: 'GET',
                    signal: controller.signal,
                    headers: {
                        authorization: `Bearer ${uToken}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (!res.ok) {
                setLlamadasActivas([]);
                return;
            }
            const data = await res.json();
            if (!Array.isArray(data)) {
                setLlamadasActivas([]);
                return;
            }
            setLlamadasActivas(data.map((item, index) => ({
                    id: item.llamada1 ?? `row-${index}`,
                    codigoArea: item.codigoArea,
                    inicialesArea: item.inicialesArea,
                    colorArea: item.color,
                    nombre: item.nombre,
                    estatus: item.estatus,
                    nivel: item.nivel,
                    fechaHoraInicio: item.fechaHoraInicio ? new Date(item.fechaHoraInicio) : null,
                    fechaHoraRespuesta: item.fechaHoraRespuesta ? new Date(item.fechaHoraRespuesta) : null,
                    tiempoRespuesta: item.tiempoRespuesta,
                    tiempoReparacion: item.tiempoReparacion,
                    estatusText: item.estatusText,
                    tipoLlamada: item.tipoLlamada,
                    celda: item.celda ?? item.nombreCelda ?? '',
                })));
        } catch (e) {
            if (e.name !== 'AbortError') {
                // evita congelar la vista si el servicio falla
            }
        }
    }, []);

    const getPromedioYResumen = useCallback(async (uToken) => {
        promedioResumenAbortRef.current?.abort();
        const controller = new AbortController();
        promedioResumenAbortRef.current = controller;
        try {
            const params = new URLSearchParams();
            if (selectedHierAreaRef.current) params.append('Area', selectedHierAreaRef.current.area1);
            if (selectedHierLineaRef.current) params.append('Linea', selectedHierLineaRef.current.id);
            if (selectedHierCeldaRef.current) params.append('Celda', selectedHierCeldaRef.current.id);
            params.append('CantHoras', intervaloRef.current);
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/Informes/GetPromedioYResumen?${params.toString()}`,
                {
                    method: 'GET',
                    signal: controller.signal,
                    headers: { authorization: `Bearer ${uToken}`, Accept: 'application/json' },
                }
            );
            if (!res.ok) return;
            const data = await res.json();
            setPromedioResumen(data);
        } catch (e) {
            if (e.name !== 'AbortError') { /* ignore */ }
        }
    }, []);

    useEffect(() => {
        getCeldas(tokenR.current);
        getLlamadas(tokenR.current);
        getLlamadasActivas(tokenR.current);
        getPromedioYResumen(tokenR.current);
        getAreas(tokenR.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-select area when there is exactly one option
    useEffect(() => {
        if (hierAreas.length === 1 && !selectedHierArea) {
            setSelectedHierArea(hierAreas[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hierAreas]);

    // Auto-select line when there is exactly one option
    useEffect(() => {
        if (hierLinesByArea.length === 1 && !selectedHierLinea) {
            setSelectedHierLinea(hierLinesByArea[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hierLinesByArea]);

    // Re-fetch when hierarchy changes
    useEffect(() => {
        if (llamadasRef.current.length > 0) {
            getStatusLinea(llamadasRef.current, tokenR.current);
        }
        getLlamadasActivas(tokenR.current);
        getEstyCont(tokenR.current);
        getPromedioYResumen(tokenR.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHierArea, selectedHierLinea, selectedHierCelda]);

    // Intervalo de actualización cada 15 segundos
    useEffect(() => {
        const interval = setInterval(() => {
            if (llamadasRef.current.length > 0) {
                getStatusLinea(llamadasRef.current, tokenR.current);
            }
            getLlamadasActivas(tokenR.current);
            getEstyCont(tokenR.current);
            getPromedioYResumen(tokenR.current);
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getEstyCont, getStatusLinea, getLlamadasActivas, getPromedioYResumen]);

    // Actualizar cuando cambia el intervalo (solo cuando el usuario cambia la pestaña)
    useEffect(() => {
        if (llamadasRef.current.length > 0) {
            getStatusLinea(llamadasRef.current, tokenR.current);
        }
        getPromedioYResumen(tokenR.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervalo, getStatusLinea, getPromedioYResumen]);

    const MonitoreoValues = useMemo(() => {
        return {
            celda,
            setCelda,
            celdas,
            statusLinea,
            intervalo,
            setIntervalo,
            isLoadingTable,
            setIsLoadingTable,
            eficiYUti,
            dataP,
            setDataP,
            minX,
            setMinX,
            maxX,
            setMaxX,
            dataEfi,
            setDataEfi,
            dataUti,
            setDataUti,
            xEfiUti,
            setXEfiUti,
            value,
            setValue,
            corridaActual,
            setCorridaActual,
            isCorridaActive,
            corridaRequiresLinea,
            llamadasActivas,
            isLoadingStatusLinea,
            promedioResumen,
            // Hierarchy
            hierAreas,
            hierLinesByArea,
            hierCeldasList,
            selectedHierArea,
            setSelectedHierArea,
            selectedHierLinea,
            setSelectedHierLinea,
            selectedHierCelda,
            setSelectedHierCelda,
        };
    }, [
        celda,
        celdas,
        statusLinea,
        intervalo,
        isLoadingTable,
        eficiYUti,
        dataP,
        minX,
        maxX,
        dataEfi,
        dataUti,
        xEfiUti,
        value,
        corridaActual,
        isCorridaActive,
        corridaRequiresLinea,
        llamadasActivas,
        isLoadingStatusLinea,
        promedioResumen,
        hierAreas,
        hierLinesByArea,
        hierCeldasList,
        selectedHierArea,
        setSelectedHierArea,
        selectedHierLinea,
        setSelectedHierLinea,
        selectedHierCelda,
        setSelectedHierCelda,
    ]);

    return <MonitoreoContext.Provider value={MonitoreoValues} {...props} />;
}

export function useMonitoreo() {
    const context = useContext(MonitoreoContext);
    if (!context) {
        throw new Error('useOrdProd debe estar dentro del proveedor MonitoreoContext');
    }
    return context;
}
